// Learn hub: categories, official guides, capture reference data, and search index (single module).

/** Canonical blog URLs for official guides */
export const OFFICIAL_GUIDES_BASE = '/blog';

/** Volugraph Capture Guide — photo table, quick tip, checklist */
export const VOLOGRAPH_CAPTURE_GUIDE_PATH = `${OFFICIAL_GUIDES_BASE}/volugraph-capture-guide`;

/** Table + checklist copy for the capture guide article only */
export const CAPTURE_PHOTO_TABLE = [
  { scene: 'Small object (shoe, toy, vase)', videoDuration: '30 sec – 1 min', photos: '50' },
  { scene: 'Medium object / furniture / person', videoDuration: '1 – 2 min', photos: '100' },
  { scene: 'Large object / car / small room', videoDuration: '2 – 5 min', photos: '150–200' },
  { scene: 'Big space / whole room / complex shiny scene', videoDuration: '5 – 10 min', photos: '200+' },
];

export const CAPTURE_TIPS = [
  {
    title: 'Good lighting',
    body: 'Use bright, even light (daylight or soft indoor lights). Avoid strong shadows, direct sun glare, or dark corners. Bad light leads to noisy, low-detail results.',
  },
  {
    title: 'Sharp focus & no blur',
    body: 'Hold your phone steady or use burst mode. Fast movement or low light makes photos blurry, which causes SfM to fail or the capture to look fuzzy.',
  },
  {
    title: 'Enough overlap between photos',
    body: 'Each photo should share 60–80% of the scene with the previous one. Too little overlap causes gaps, missing parts, or broken geometry.',
  },
  {
    title: 'Varied angles',
    body: 'Walk around the object completely (360° if possible). Front-only shots give a flat result or missing back and sides. More angles mean a fuller, more realistic capture.',
  },
  {
    title: 'Distance & framing',
    body: 'Stay roughly the same distance from the object and don’t zoom in or out wildly. Keep the object filling most of the frame, not tiny in the middle.',
  },
  {
    title: 'No moving objects',
    body: 'People, cars, or leaves blowing in the wind create ghosting artifacts. A static scene works best.',
  },
  {
    title: 'High resolution',
    body: 'Use full phone camera resolution (12–48 MP). Low-res photos mean less detail in the final capture.',
  },
  {
    title: 'Clean lens',
    body: 'Wipe fingerprints and smudges off the camera lens. A dirty lens gives hazy, low-contrast photos.',
  },
];

/**
 * Topic categories: each box → /documentation/:slug → list of articles (unique `to` per row).
 * Official long-form guides (Volugraph Capture Guide, 3DGS, aerial) live only under
 * `OFFICIAL_GUIDE_LINKS` on the Learn home, not in these lists—no cross-listing the same
 * article across categories.
 */
export const LEARN_CATEGORIES = [
  {
    slug: 'frequently-asked-questions',
    title: 'Frequently Asked Questions',
    description: 'Common product questions and answers',
    icon: 'ph ph-question',
    articles: [
      {
        id: 'faq-videos',
        slug: 'what-kind-of-videos-work-best',
        title: 'What kind of video do I need to upload?',
        description: 'Record a slow, steady video moving around the subject.',
        to: '/documentation/frequently-asked-questions/what-kind-of-videos-work-best',
      },
      {
        id: 'faq-processing',
        slug: 'how-long-does-processing-take',
        title: 'How long does processing take?',
        description: 'Most captures finish in 30 minutes to an hour.',
        to: '/documentation/frequently-asked-questions/how-long-does-processing-take',
      },
      {
        id: 'faq-edit',
        slug: 'can-i-edit-my-3d-captures',
        title: 'Can I edit my 3D captures?',
        description: 'Yes, clean up, annotate, and refine at any time.',
        to: '/documentation/frequently-asked-questions/can-i-edit-my-3d-captures',
      },
      {
        id: 'faq-share',
        slug: 'can-i-share-or-embed-my-scan',
        title: 'Can I share or embed my capture?',
        description: 'Share a link or embed on any site, no install for viewers.',
        to: '/documentation/frequently-asked-questions/can-i-share-or-embed-my-scan',
      },
      {
        id: 'faq-software',
        slug: 'do-i-need-to-download-software',
        title: 'Do I need to download software?',
        description: 'No, everything runs in the browser.',
        to: '/documentation/frequently-asked-questions/do-i-need-to-download-software',
      },
    ],
  },
  {
    slug: 'the-first-project',
    title: 'The First Project',
    description: 'Learn to capture and understand the project space.',
    icon: 'ph ph-folder',
    articles: [
      {
        id: 'fp-create',
        slug: 'how-do-i-create-my-first-project',
        title: 'How do I create my first project?',
        description: 'Create a project folder, then add captures from My Studio.',
        to: '/documentation/the-first-project/how-do-i-create-my-first-project',
      },
      {
        id: 'fp-video',
        slug: 'what-video-should-i-upload',
        title: 'What video should I upload?',
        description: 'Source video format, length limits, and what works best.',
        to: '/documentation/the-first-project/what-video-should-i-upload',
      },
      {
        id: 'fp-organise',
        slug: 'how-are-projects-and-captures-organised',
        title: 'How are projects and captures organised?',
        description: 'Projects, captures, and versions in your studio.',
        to: '/documentation/the-first-project/how-are-projects-and-captures-organised',
      },
      {
        id: 'fp-free',
        slug: 'what-are-the-free-plan-limits',
        title: 'What are the free plan limits?',
        description: 'Lifetime captures, video length, and storage on Free.',
        to: '/documentation/the-first-project/what-are-the-free-plan-limits',
      },
      {
        id: 'fp-team',
        slug: 'how-do-i-invite-a-teammate',
        title: 'How do I invite a teammate to a project?',
        description: 'Viewer, Editor, and Admin roles for collaboration.',
        to: '/documentation/the-first-project/how-do-i-invite-a-teammate',
      },
    ],
  },
  {
    slug: 'the-3d-workspace',
    title: 'The 3D Workspace',
    description: 'Navigate, edit, and share 3D captures from any browser.',
    icon: 'ph ph-cube',
    articles: [
      {
        id: 'ws-navigate',
        slug: 'how-do-i-navigate-a-capture',
        title: 'How do I navigate a capture?',
        description: 'Orbit, pan, zoom, and refocus in the 3D viewer.',
        to: '/documentation/the-3d-workspace/how-do-i-navigate-a-capture',
      },
      {
        id: 'ws-annotations',
        slug: 'how-do-i-add-annotations',
        title: 'How do I add annotations?',
        description: 'Notes and markers on your 3D capture.',
        to: '/documentation/the-3d-workspace/how-do-i-add-annotations',
      },
      {
        id: 'ws-render',
        slug: 'what-are-render-modes',
        title: 'What are render modes?',
        description: 'Splat, Mixed, and Points for viewing your scene.',
        to: '/documentation/the-3d-workspace/what-are-render-modes',
      },
      {
        id: 'ws-map',
        slug: 'how-do-i-use-the-map-tab',
        title: 'How do I use the map tab?',
        description: 'Open the map view and place captures on a site.',
        to: '/documentation/the-3d-workspace/how-do-i-use-the-map-tab',
      },
      {
        id: 'ws-semantic',
        slug: 'what-is-semantic-search',
        title: 'What is semantic search?',
        description: 'Find objects in your scene with natural language.',
        to: '/documentation/the-3d-workspace/what-is-semantic-search',
      },
    ],
  },
  {
    slug: 'use-cases',
    title: 'Use Cases',
    description: 'How aerial survey crews, film location scouts, and product teams use Volugraph.',
    icon: 'ph ph-lightbulb',
    articles: [
      {
        id: 'uc-aerial',
        slug: 'aerial-surveying',
        title: 'Aerial surveying with Gaussian splats',
        description: 'Capture a site from a drone and share a navigable 3D model in the browser.',
        to: '/documentation/use-cases/aerial-surveying',
      },
      {
        id: 'uc-film',
        slug: 'film-location-scouting',
        title: 'Film and location scouting',
        description: 'Document sets and locations as interactive 3D captures your whole team can explore.',
        to: '/documentation/use-cases/film-location-scouting',
      },
      {
        id: 'uc-ecommerce',
        slug: 'e-commerce-product-capture',
        title: 'E-commerce product capture',
        description: 'Turn products into shareable 3D models for listings, pitches, and showrooms.',
        to: '/documentation/use-cases/e-commerce-product-capture',
      },
    ],
  },
  {
    slug: 'billing',
    title: 'Billing & Plans',
    description: 'Understand payment plans and allowances.',
    icon: 'ph ph-credit-card',
    articles: [
      {
        id: 'bill-plans',
        slug: 'what-plans-does-volugraph-offer',
        title: 'What plans does Volugraph offer?',
        description: 'Free, Professional, Studio, and Enterprise at a glance.',
        to: '/documentation/billing/what-plans-does-volugraph-offer',
      },
      {
        id: 'bill-limits',
        slug: 'how-do-capture-limits-work',
        title: 'How do capture limits work?',
        description: 'Rolling windows, lifetime free tier, and what counts as a capture.',
        to: '/documentation/billing/how-do-capture-limits-work',
      },
      {
        id: 'bill-private',
        slug: 'what-is-a-private-share-link',
        title: 'What is a private share link?',
        description: 'Password-protected links and which plans include them.',
        to: '/documentation/billing/what-is-a-private-share-link',
      },
      {
        id: 'bill-download',
        slug: 'can-i-download-my-capture',
        title: 'Can I download my capture?',
        description: '.ply export and plan requirements.',
        to: '/documentation/billing/can-i-download-my-capture',
      },
      {
        id: 'bill-upgrade',
        slug: 'how-do-i-upgrade-my-plan',
        title: 'How do I upgrade my plan?',
        description: 'Billing page, checkout, and changing your subscription.',
        to: '/documentation/billing/how-do-i-upgrade-my-plan',
      },
    ],
  },
  {
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Solutions to common capture or project issues.',
    icon: 'ph ph-wrench',
    articles: [
      {
        id: 'ts-upload',
        slug: 'my-upload-is-failing',
        title: 'My upload is failing',
        description: 'File type, size, duration, and plan checks.',
        to: '/documentation/troubleshooting/my-upload-is-failing',
      },
      {
        id: 'ts-stuck',
        slug: 'my-capture-is-stuck-processing',
        title: 'My capture is stuck processing',
        description: 'Queues, wait times, and when to wait longer.',
        to: '/documentation/troubleshooting/my-capture-is-stuck-processing',
      },
      {
        id: 'ts-failed',
        slug: 'my-capture-failed-to-process',
        title: 'My capture failed to process',
        description: 'Reading the pipeline stages and what to try next.',
        to: '/documentation/troubleshooting/my-capture-failed-to-process',
      },
      {
        id: 'ts-share',
        slug: 'my-share-link-is-not-working',
        title: 'My share link is not working',
        description: 'Password, token, and loading issues for viewers.',
        to: '/documentation/troubleshooting/my-share-link-is-not-working',
      },
      {
        id: 'ts-viewer',
        slug: 'the-3d-viewer-is-not-loading',
        title: 'The 3D viewer is not loading',
        description: 'WebGL, memory, and browser tips.',
        to: '/documentation/troubleshooting/the-3d-viewer-is-not-loading',
      },
    ],
  },
];

/** Learn home “Official guides” cards + search */
export const OFFICIAL_GUIDE_LINKS = [
  {
    id: 'volugraph-capture-guide',
    to: VOLOGRAPH_CAPTURE_GUIDE_PATH,
    title: 'How To Capture Guide',
    description: 'Camera settings, flight path, photo counts, and the full eight-point checklist.',
    icon: 'ph ph-notebook',
  },
  {
    id: 'aerial-footage-to-3d-gaussian-splat',
    to: `${OFFICIAL_GUIDES_BASE}/aerial-footage-to-3d-gaussian-splat`,
    title: 'Aerial Footage To 3D Gaussian Splat',
    description: 'Turn drone and aerial video into an interactive 3D Gaussian splat you can share in the browser.',
    icon: 'ph ph-airplane-tilt',
  },
  {
    id: 'vertical-aerial-surveying',
    to: '/aerial-surveying',
    title: 'Aerial Surveying',
    description: 'Turn drone and aerial video into interactive 3D Gaussian splats for surveying crews.',
    icon: 'ph ph-airplane-tilt',
  },
  {
    id: 'vertical-location-visualisation',
    to: '/location-visualisation',
    title: 'Real Estate Visualisation',
    description: 'Capture venues, sets, and properties as explorable 3D locations from video.',
    icon: 'ph ph-map-pin',
  },
  {
    id: 'vertical-ecommerce-3d',
    to: '/ecommerce-3d',
    title: 'E-Commerce 3D Products',
    description: 'Turn product video into interactive 3D models for listings and online retail.',
    icon: 'ph ph-shopping-bag',
  },
];

export const OFFICIAL_ARTICLE_SLUGS = {
  capture: 'volugraph-capture-guide',
  aerialFootage: 'aerial-footage-to-3d-gaussian-splat',
  ecommerce: 'e-commerce-products',
};

function buildLearnSearchIndex() {
  const items = [];
  for (const cat of LEARN_CATEGORIES) {
    items.push({
      id: `topic-${cat.slug}`,
      title: cat.title,
      description: cat.description,
      to: `/documentation/${cat.slug}`,
    });
    for (const a of cat.articles) {
      items.push({
        id: a.id,
        title: a.title,
        description: `${cat.title}: ${a.description}`,
        to: a.to,
      });
    }
  }
  for (const g of OFFICIAL_GUIDE_LINKS) {
    items.push({
      id: `og-${g.id}`,
      title: g.title,
      description: g.description,
      to: g.to,
    });
  }
  return items;
}

export const LEARN_SEARCH_INDEX = buildLearnSearchIndex();

/**
 * @param {string} query
 * @returns {typeof LEARN_SEARCH_INDEX}
 */
export function filterLearnSearchIndex(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return LEARN_SEARCH_INDEX.filter((item) => {
    const blob = `${item.title} ${item.description}`.toLowerCase();
    return blob.includes(q);
  });
}

export function getCategoryBySlug(slug) {
  if (!slug) return null;
  return LEARN_CATEGORIES.find((c) => c.slug === slug) || null;
}

export function getArticleBySlug(categorySlug, articleSlug) {
  const cat = getCategoryBySlug(categorySlug);
  if (!cat) return null;
  return cat.articles.find((a) => a.slug === articleSlug) || null;
}
